import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ChefMediaGallery = ({ user, profile, onUpdate }) => {
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [lunchPhotos, setLunchPhotos] = useState([]);
  const [dinnerPhotos, setDinnerPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (profile) {
      // Separate lunch and dinner photos
      const allPhotos = profile.meal_photos || profile.photo_urls || [];
      // If photos don't have meal_type, show them in lunch by default (backward compatibility)
      setLunchPhotos(allPhotos.filter(p => !p.meal_type || p.meal_type === 'lunch'));
      setDinnerPhotos(allPhotos.filter(p => p.meal_type === 'dinner'));
      setVideos(profile.video_urls || []);
    }
  }, [profile]);

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size only (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setMessage('❌ Profile photo must be less than 10MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', user.id);
    formData.append('photo_type', 'profile');

    try {
      await axios.post('/chef/photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMessage('✅ Profile photo uploaded successfully!');
      onUpdate();
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.detail || 'Failed to upload profile photo'));
    } finally {
      setUploading(false);
    }
  };

  const handleMealPhotoUpload = async (e, mealType) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check total count for specific meal type
    const currentCount = mealType === 'lunch' ? lunchPhotos.length : dinnerPhotos.length;
    const newCount = currentCount + files.length;

    if (newCount > 21) {
      setMessage(`❌ Maximum 21 ${mealType} photos allowed. You have ${currentCount}, trying to add ${files.length}.`);
      return;
    }

    if (newCount < 14) {
      setMessage(`⚠️ You're uploading ${newCount} ${mealType} photos. Minimum 14 recommended for best customer impression.`);
    }

    // Validate file size only
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        setMessage(`❌ ${file.name} is too large. Max 10MB per file.`);
        return;
      }
    }

    setUploading(true);
    setMessage('📤 Uploading photos...');
    
    try {
      let uploadedCount = 0;
      
      for (const file of files) {
        // Ask for dish name and description
        const dishName = prompt(`What is the name of this dish?\n(${file.name})`);
        if (!dishName) {
          alert('Dish name is required. Photo not uploaded.');
          continue;
        }
        
        const chefDescription = prompt(`Add a short description of "${dishName}":\n(e.g., "Grilled salmon with lemon butter sauce")`);
        if (!chefDescription) {
          alert('Description is required. Photo not uploaded.');
          continue;
        }
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('user_id', user.id);
        formData.append('photo_type', 'meal');
        formData.append('meal_type', mealType);
        formData.append('dish_name', dishName);
        formData.append('chef_description', chefDescription);

        const response = await axios.post('/chef/photo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedCount++;
        setMessage(`📤 Uploaded: "${dishName}" - ${uploadedCount}/${files.length}`);
        
        // Small delay to show progress
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      setMessage(`✅ ${uploadedCount} meal photo(s) uploaded successfully!`);
      onUpdate();
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.detail || 'Failed to upload meal photos'));
    } finally {
      setUploading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check video count
    if (videos.length + files.length > 2) {
      setMessage(`❌ Maximum 2 videos allowed. You already have ${videos.length} video(s).`);
      return;
    }

    // Validate videos
    for (const file of files) {
      if (!file.type.startsWith('video/')) {
        setMessage('❌ All files must be videos');
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        setMessage(`❌ ${file.name} is too large. Max 50MB per video (recommended: 30 seconds).`);
        return;
      }
    }

    setUploading(true);

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('user_id', user.id);

        await axios.post('/chef/video', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      setMessage(`✅ ${files.length} video(s) uploaded successfully!`);
      onUpdate();
    } catch (err) {
      setMessage('❌ ' + (err.response?.data?.detail || 'Failed to upload videos'));
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoUrl) => {
    if (!window.confirm('Delete this photo?')) return;

    try {
      await axios.delete(`/chef/photo`, {
        data: { user_id: user.id, photo_url: photoUrl }
      });
      setMessage('✅ Photo deleted');
      onUpdate();
    } catch (err) {
      setMessage('❌ Failed to delete photo');
    }
  };

  const handleDeleteVideo = async (videoUrl) => {
    if (!window.confirm('Delete this video?')) return;

    try {
      await axios.delete(`/chef/video`, {
        data: { user_id: user.id, video_url: videoUrl }
      });
      setMessage('✅ Video deleted');
      onUpdate();
    } catch (err) {
      setMessage('❌ Failed to delete video');
    }
  };

  return (
    <div className="space-y-8">
      {/* Profile Photo Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">📸 Profile Photo</h2>
        <p className="text-sm text-gray-600 mb-4">
          Upload a professional headshot or photo of yourself in chef attire
        </p>
        
        <div className="flex items-center gap-4">
          <input
            type="file"
            onChange={handleProfilePhotoUpload}
            disabled={uploading}
            className="text-sm"
          />
        </div>
        
        <p className="text-xs text-gray-500 mt-2">
          Max size: 10MB | All file types accepted
        </p>
      </div>

      {/* Lunch Menu Photos */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">🌞 Lunch Menu Photos</h2>
            <p className="text-sm text-gray-600 mt-1">
              Upload 14-21 photos of your lunch dishes ({lunchPhotos.length}/21)
            </p>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
            lunchPhotos.length >= 14 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
          }`}>
            {lunchPhotos.length >= 14 ? '✅ Complete' : `⚠️ Need ${14 - lunchPhotos.length} more`}
          </div>
        </div>

        <div className="mb-4">
          <input
            type="file"
            multiple
            onChange={(e) => handleMealPhotoUpload(e, 'lunch')}
            disabled={uploading || lunchPhotos.length >= 21}
            className="text-sm"
          />
          <p className="text-xs text-gray-500 mt-2">
            Max: 10MB per file | All file types accepted | Select multiple to upload at once
          </p>
        </div>

        {/* Photo Grid with AI Info */}
        {lunchPhotos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
            {lunchPhotos.map((photo, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition">
                <div className="relative group">
                  <img
                    src={photo.url}
                    alt={photo.dish_name || `Meal ${index + 1}`}
                    className="w-full h-48 object-cover"
                  />
                  <button
                    onClick={() => handleDeletePhoto(photo.url)}
                    className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition shadow-lg"
                    title="Delete photo"
                  >
                    🗑️
                  </button>
                </div>
                
                <div className="p-4">
                  <h4 className="font-bold text-lg text-gray-900 mb-2">
                    {photo.dish_name || 'Delicious Dish'}
                  </h4>
                  
                  {photo.ai_description && (
                    <p className="text-sm text-gray-700 mb-2">
                      <span className="font-semibold">🤖 AI:</span> {photo.ai_description}
                    </p>
                  )}
                  
                  {photo.chef_description && (
                    <p className="text-sm text-orange-700 mb-2 border-l-2 border-orange-500 pl-2">
                      <span className="font-semibold">👨‍🍳 Chef:</span> {photo.chef_description}
                    </p>
                  )}
                  
                  {photo.cooking_method && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-900 font-semibold">
                        How it's made
                      </summary>
                      <p className="text-xs text-gray-600 mt-2 pl-2">
                        {photo.cooking_method}
                      </p>
                    </details>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (profile?.meal_photos || profile?.photo_urls || []).length > 0 ? (
          // Fallback for old photo format (just URLs)
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
            {(profile?.meal_photos || profile?.photo_urls || []).map((photo, index) => (
              <div key={index} className="relative group">
                <img
                  src={photo}
                  alt={`Meal ${index + 1}`}
                  className="w-full h-40 object-cover rounded-lg"
                />
                <button
                  onClick={() => handleDeletePhoto(photo)}
                  className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition"
                  title="Delete photo"
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <p className="text-gray-500">No lunch photos yet. Upload your first lunch dishes!</p>
            <p className="text-xs text-gray-400 mt-2">🤖 AI will automatically identify dishes and explain how they're made!</p>
          </div>
        )}
      </div>

      {/* Dinner Menu Photos */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">🌙 Dinner Menu Photos</h2>
            <p className="text-sm text-gray-600 mt-1">
              Upload 14-21 photos of your dinner dishes ({dinnerPhotos.length}/21)
            </p>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
            dinnerPhotos.length >= 14 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
          }`}>
            {dinnerPhotos.length >= 14 ? '✅ Complete' : `⚠️ Need ${14 - dinnerPhotos.length} more`}
          </div>
        </div>

        <div className="mb-4">
          <input
            type="file"
            multiple
            onChange={(e) => handleMealPhotoUpload(e, 'dinner')}
            disabled={uploading || dinnerPhotos.length >= 21}
            className="text-sm"
          />
          <p className="text-xs text-gray-500 mt-2">
            Max: 10MB per file | All file types accepted | Select multiple to upload at once
          </p>
        </div>

        {/* Photo Grid with AI Info */}
        {dinnerPhotos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
            {dinnerPhotos.map((photo, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition">
                <div className="relative group">
                  <img
                    src={photo.url}
                    alt={photo.dish_name || `Dinner ${index + 1}`}
                    className="w-full h-48 object-cover"
                  />
                  <button
                    onClick={() => handleDeletePhoto(photo.url)}
                    className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition shadow-lg"
                    title="Delete photo"
                  >
                    🗑️
                  </button>
                  {photo.ai_generated && (
                    <div className="absolute top-2 left-2 bg-purple-600 text-white px-2 py-1 rounded-full text-xs font-semibold">
                      🤖 AI Analyzed
                    </div>
                  )}
                </div>
                
                <div className="p-4">
                  <h4 className="font-bold text-lg text-gray-900 mb-2">
                    {photo.dish_name || 'Delicious Dish'}
                  </h4>
                  
                  {/* Chef's Description */}
                  {photo.chef_description && (
                    <div className="mb-3">
                      <p className="text-sm text-gray-700">
                        {photo.chef_description}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <p className="text-gray-500">No dinner photos yet. Upload your first dinner dishes!</p>
            <p className="text-xs text-gray-400 mt-2">Upload photos with your dish names and descriptions</p>
          </div>
        )}
      </div>

      {/* Videos Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">🎥 Introduction Videos</h2>
            <p className="text-sm text-gray-600 mt-1">
              Upload up to 2 videos (30 seconds each recommended) ({videos.length}/2)
            </p>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
            videos.length === 2 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {videos.length === 2 ? '✅ Complete' : `${videos.length}/2`}
          </div>
        </div>

        <div className="mb-4">
          <input
            type="file"
            accept="video/*"
            multiple={videos.length < 2}
            onChange={handleVideoUpload}
            disabled={uploading || videos.length >= 2}
            className="text-sm"
          />
          <p className="text-xs text-gray-500 mt-2">
            Max: 50MB per video | Recommended: 30 seconds, MP4 format
          </p>
        </div>

        {/* Video Grid */}
        {videos.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {videos.map((video, index) => (
              <div key={index} className="relative group">
                <video
                  src={video}
                  controls
                  className="w-full h-48 object-cover rounded-lg bg-black"
                >
                  Your browser doesn't support video playback.
                </video>
                <button
                  onClick={() => handleDeleteVideo(video)}
                  className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition"
                  title="Delete video"
                >
                  🗑️
                </button>
                <div className="mt-2 text-sm text-gray-600 font-medium">
                  Video {index + 1}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <p className="text-gray-500">No videos yet. Introduce yourself to customers!</p>
          </div>
        )}
      </div>

      {/* Status Messages */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.includes('✅') ? 'bg-green-50 text-green-800' :
          message.includes('⚠️') ? 'bg-yellow-50 text-yellow-800' :
          'bg-red-50 text-red-800'
        }`}>
          {message}
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
            <p className="text-gray-700 font-medium">Uploading...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChefMediaGallery;
